import {useMutation} from '@apollo/react-hooks';
import moment from 'moment';

import {htmlEncode} from 'nutshell-core/utils/html-encode';

import type {
    CreateComment as CreateCommentMutation,
    CreateCommentVariables as CreateCommentMutationVariables,
    SessionUser,
} from 'nutshell-graphql-types';

import COMMENT_CREATE from '../../../timeline/comments/graphql/mutations/comment-create.graphql';

import ACTIVITY_MODAL_FRAGMENT from '../../graphql/fragments/activity-for-modal-fragment.graphql';

export function useCreateComment(): (arg1: {
    activityId: string;
    value: string;
    user: SessionUser;
}) => Promise<any> {
    const [createComment] = useMutation<CreateCommentMutation, CreateCommentMutationVariables>(
        COMMENT_CREATE
    );

    return (commentInput: {activityId: string; value: string; user: SessionUser}) => {
        return createComment({
            variables: {
                input: {
                    comment: {
                        parentId: commentInput.activityId,
                        value: commentInput.value,
                    },
                },
            },
            optimisticResponse: {
                __typename: 'Mutation',
                commentCreate: {
                    __typename: 'CreateCommentPayload',
                    comment: {
                        __typename: 'Comment',
                        id: `temp-${Date.now()}-${Math.random()}`,
                        createdTime: moment().unix(),
                        creatorUser: {
                            __typename: 'User',
                            id: commentInput.user.id,
                            name: commentInput.user.name,
                            avatarUrl: commentInput.user.avatarUrl,
                            htmlUrl: commentInput.user.htmlUrl,
                            initials: commentInput.user.initials,
                        },
                        isDeleteable: false,
                        // @ts-expect-error
                        body: null,
                        bodyHtml: htmlEncode(commentInput.value),
                        reactions: [],
                        changeLogEntry: {
                            __typename: 'TimelineEvent',
                            id: '1-events',
                        },
                    },
                },
            },
            update: (proxy, mutationResults) => {
                const activityFragment = proxy.readFragment({
                    id: commentInput.activityId,
                    fragmentName: 'ActivityModalFragment',
                    fragment: ACTIVITY_MODAL_FRAGMENT,
                });

                // @ts-expect-error
                if (activityFragment && activityFragment.comments) {
                    const newComment =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.commentCreate &&
                        mutationResults.data.commentCreate.comment;

                    if (!newComment) return;

                    proxy.writeFragment({
                        id: commentInput.activityId,
                        fragment: ACTIVITY_MODAL_FRAGMENT,
                        fragmentName: 'ActivityModalFragment',
                        data: {
                            ...activityFragment,
                            __typename: 'Activity',
                            comments: {
                                __typename: 'CommentConnection',
                                // @ts-expect-error
                                totalCount: activityFragment.comments.totalCount + 1,
                                // @ts-expect-error
                                edges: activityFragment.comments.edges.concat({
                                    __typename: 'CommentEdge',
                                    node: newComment,
                                }),
                            },
                        },
                    });
                }
            },
        });
    };
}
